import { useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertCircle,
  ChevronRight,
  Edit,
  GripVertical,
  Images,
  Inbox,
  Plus,
  RefreshCw,
  Trash2,
  Youtube,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import {
  isYouTubeQuotaError,
  useCreateYtPlaylist,
  useDeleteYtPlaylist,
  usePollYtPlaylist,
  useRefreshAllYtThumbnails,
  useReorderYtPlaylists,
  ytErrorMessage,
  ytPlaylistsQueryOptions,
} from '@/lib/youtube-admin';
import { PlaylistSettingsDialog } from '@/components/videos/playlist-settings-dialog';
import { QuotaBanner } from '@/components/videos/quota-banner';
import type { YoutubePlaylistListItem } from '@news-cms/shared';

export const Route = createFileRoute('/_authenticated/videos/')({
  component: VideosPage,
});

function VideosPage() {
  const { user } = useSession();
  const canManage = hasPermission(user, 'feed.manage');
  const query = useQuery(ytPlaylistsQueryOptions);
  const reorder = useReorderYtPlaylists();
  const refreshThumbs = useRefreshAllYtThumbnails();
  const [createOpen, setCreateOpen] = useState(false);
  const [thumbResult, setThumbResult] = useState<string | null>(null);
  // 5px activation distance so clicking action buttons inside a card
  // doesn't start a drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const items = query.data ?? [];
  // useMemo must run on every render to satisfy Rules of Hooks; the
  // permission gate's early return happens AFTER this so the hook
  // order stays stable across renders.
  const orderedIds = useMemo(() => items.map((p) => p.id), [items]);

  if (!canManage) {
    return (
      <div className="p-8 text-center text-sm text-slate-500">
        ไม่มีสิทธิ์เข้าถึง — ต้องมี feed.manage
      </div>
    );
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = orderedIds.indexOf(String(active.id));
    const to = orderedIds.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    reorder.mutate(arrayMove(orderedIds, from, to));
  };

  // One-shot: upgrade every stored video's thumbnail to the largest available
  // size. Backfills rows the poll never re-fetches (onConflictDoNothing).
  const handleRefreshThumbs = () => {
    if (
      !window.confirm(
        'รีเฟรช thumbnail ของวิดีโอทั้งหมดให้เป็นขนาดใหญ่สุด? (เรียก YouTube API — ใช้โควตาเล็กน้อย)',
      )
    )
      return;
    setThumbResult(null);
    refreshThumbs.mutate(undefined, {
      onSuccess: (r) =>
        setThumbResult(
          `รีเฟรชแล้ว: อัปเดต ${r.refreshed}/${r.scanned} วิดีโอ (ใช้ ${r.quotaUnits} quota)`,
        ),
      onError: (e) => setThumbResult(`ผิดพลาด: ${ytErrorMessage(e)}`),
    });
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              <Youtube className="text-brand-600 h-6 w-6" />
              วิดีโอ YouTube
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              จัดการ playlist ({items.length}) และวิดีโอที่จะแสดงบนเว็บ public
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            {items.length > 0 && (
              <Button
                variant="outline"
                onClick={handleRefreshThumbs}
                disabled={refreshThumbs.isPending}
                title="รีเฟรช thumbnail วิดีโอทั้งหมดให้เป็นขนาดใหญ่สุด (ครั้งเดียวพอ)"
              >
                {refreshThumbs.isPending ? (
                  <RefreshCw className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Images className="mr-1.5 h-4 w-4" />
                )}
                รีเฟรชขนาดรูป
              </Button>
            )}
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" />
              เพิ่ม Playlist
            </Button>
          </div>
        </div>

        <QuotaBanner />

        {thumbResult && (
          <Alert>
            <Images className="h-4 w-4" />
            <AlertDescription>{thumbResult}</AlertDescription>
          </Alert>
        )}

        {query.isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{ytErrorMessage(query.error)}</AlertDescription>
          </Alert>
        )}

        {query.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            กำลังโหลด...
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
            <Inbox className="h-12 w-12 text-slate-300" />
            <p className="text-sm text-slate-500">ยังไม่มี playlist</p>
            <p className="text-xs text-slate-400">กด "เพิ่ม Playlist" เพื่อเริ่ม</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={orderedIds} strategy={rectSortingStrategy}>
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((p) => (
                  <PlaylistCard key={p.id} playlist={p} />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <CreatePlaylistDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function PlaylistCard({ playlist }: { playlist: YoutubePlaylistListItem }) {
  const poll = usePollYtPlaylist(playlist.id);
  const del = useDeleteYtPlaylist();
  const [editOpen, setEditOpen] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: playlist.id,
  });
  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleDelete = () => {
    if (!window.confirm(`ลบ playlist "${playlist.name}" และวิดีโอทั้งหมด?`)) return;
    del.mutate(playlist.id);
  };

  const lastPolled = playlist.lastPolledAt
    ? new Date(playlist.lastPolledAt).toLocaleString('th-TH', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

  return (
    <li
      ref={setNodeRef}
      style={sortableStyle}
      className={`flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 transition hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900 ${
        isDragging ? 'z-10 shadow-lg' : ''
      }`}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="mt-0.5 cursor-grab text-slate-400 hover:text-slate-600 active:cursor-grabbing dark:hover:text-slate-200"
          aria-label="ลากเรียง"
          title="ลากเพื่อเรียงลำดับการแสดงบนเว็บ public"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <Link
          to="/videos/$playlistId"
          params={{ playlistId: playlist.id }}
          className="flex flex-1 items-start gap-3"
        >
          {playlist.thumbnailUrl ? (
            <img
              src={playlist.thumbnailUrl}
              alt=""
              loading="lazy"
              className="h-20 w-28 shrink-0 rounded object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.visibility = 'hidden';
              }}
            />
          ) : (
            <div className="flex h-20 w-28 shrink-0 items-center justify-center rounded bg-slate-100 dark:bg-slate-800">
              <Youtube className="h-8 w-8 text-slate-300" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <div className="line-clamp-2 text-sm font-medium text-slate-900 dark:text-slate-50">
                {playlist.name}
              </div>
              {playlist.isSystem && (
                <span
                  className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                  title="Playlist ระบบ — ลบไม่ได้, รายการมาจากอัลกอริทึม (ห้ามแก้ด้วยมือ)"
                >
                  🌟 ระบบ
                </span>
              )}
            </div>
            <div className="mt-1 text-xs text-slate-500">{playlist.videoCount} วิดีโอ</div>
            <div className="mt-1 truncate text-[11px] text-slate-400">slug: {playlist.slug}</div>
            {!playlist.isActive && (
              <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                ปิดใช้งาน
              </span>
            )}
          </div>
          <ChevronRight className="h-4 w-4 text-slate-400" />
        </Link>
      </div>

      {playlist.lastError &&
        (isYouTubeQuotaError(playlist.lastError) ? (
          // Quota errors aren't bugs to triage — operator can see the
          // banner at the top of the page. Render a calmer amber chip
          // so the card doesn't look broken; full body text in title.
          <div
            className="rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
            title={playlist.lastError}
          >
            ⏳ ข้าม poll: YouTube API quota หมดวันนี้
          </div>
        ) : (
          <div className="rounded-md bg-rose-50 px-2 py-1 text-[11px] text-rose-700 dark:bg-rose-950/30 dark:text-rose-300">
            {playlist.lastError}
          </div>
        ))}

      <div className="flex items-center justify-between text-[11px] text-slate-400">
        <span>Poll ล่าสุด: {lastPolled}</span>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() => poll.mutate()}
            disabled={poll.isPending}
            title="Poll now"
          >
            <RefreshCw className={`h-3 w-3 ${poll.isPending ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} title="แก้ไข">
            <Edit className="h-3 w-3" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleDelete}
            // Built-in playlists (the popular row) can't be deleted —
            // backend returns 409 SYSTEM_PLAYLIST. Disable here so the
            // user doesn't burn a click on a confirm dialog that'd
            // immediately toast an error.
            disabled={del.isPending || playlist.isSystem}
            title={playlist.isSystem ? 'ลบ playlist ระบบไม่ได้' : 'ลบ'}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      <PlaylistSettingsDialog playlist={playlist} open={editOpen} onOpenChange={setEditOpen} />
    </li>
  );
}

/* ---------- Create dialog ---------- */

/** Live preview parser — mirrors `parseYouTubePlaylistUrl` in
 * apps/api/src/lib/youtube-fetch.ts. Kept inline rather than imported so
 * this dialog has no extra dep on the api package; backend re-validates
 * either way. */
function parsePlaylistInput(raw: string): { kind: 'id' | 'url'; value: string } | null {
  const v = raw.trim();
  if (!v) return null;
  // Bare playlist id (PL..., UU..., LL..., etc.) — alnum/_/- 8-128 chars.
  if (/^[A-Za-z0-9_-]{8,128}$/.test(v) && !v.startsWith('http')) {
    return { kind: 'id', value: v };
  }
  // URL form — must have ?list=<id>.
  try {
    const u = new URL(v);
    const host = u.hostname.replace(/^www\./, '');
    if (!['youtube.com', 'youtube-nocookie.com', 'm.youtube.com'].includes(host)) return null;
    const list = u.searchParams.get('list');
    if (list && /^[A-Za-z0-9_-]{8,128}$/.test(list)) {
      return { kind: 'url', value: list };
    }
  } catch {
    return null;
  }
  return null;
}

function CreatePlaylistDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const create = useCreateYtPlaylist();
  const [name, setName] = useState('');
  const [playlistInput, setPlaylistInput] = useState('');
  // Default 720 min (12 h) keeps the YouTube Data API daily quota
  // (10k units default) under control — at ~2 units/tick a playlist
  // costs ~4 units/day instead of ~576 at the old 60-min default.
  const [interval, setInterval] = useState('720');
  const [submitError, setSubmitError] = useState<unknown>(null);
  const parsedPreview = parsePlaylistInput(playlistInput);

  const reset = () => {
    setName('');
    setPlaylistInput('');
    setInterval('720');
    setSubmitError(null);
  };

  const handleSave = () => {
    setSubmitError(null);
    create.mutate(
      {
        name: name.trim(),
        playlistInput: playlistInput.trim(),
        fetchIntervalMinutes: Math.max(5, Math.min(1440, Number(interval) || 720)),
        isActive: true,
        sortOrder: 0,
        description: null,
      },
      {
        onSuccess: () => {
          reset();
          onOpenChange(false);
        },
        onError: (e) => setSubmitError(e),
      },
    );
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>เพิ่ม Playlist</DialogTitle>
          <DialogDescription>
            ใส่ได้ทั้ง URL playlist (เช่น{' '}
            <code className="text-[11px]">https://youtube.com/playlist?list=PL...</code>) หรือ
            playlist ID ล้วนๆ (เช่น <code className="text-[11px]">PLxxxxxxxx</code>)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="yt-name">ชื่อ</Label>
            <Input
              id="yt-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder="เช่น ข่าวกีฬา"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="yt-playlist-input">URL playlist หรือ Playlist ID</Label>
            <Input
              id="yt-playlist-input"
              value={playlistInput}
              onChange={(e) => setPlaylistInput(e.target.value)}
              spellCheck={false}
              autoComplete="off"
              placeholder="https://youtube.com/playlist?list=PL... หรือ PLxxxxxxxx"
            />
            {playlistInput.trim() &&
              (parsedPreview ? (
                <p className="text-[11px] text-emerald-700 dark:text-emerald-400">
                  ✓ {parsedPreview.kind === 'url' ? 'จาก URL' : 'Playlist ID'}:{' '}
                  <span className="font-mono">{parsedPreview.value}</span>
                </p>
              ) : (
                <p className="text-[11px] text-red-700 dark:text-red-400">
                  ✗ ไม่ใช่ URL หรือ Playlist ID ที่ถูกต้อง
                </p>
              ))}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="yt-interval">รอบ poll (นาที)</Label>
            <Input
              id="yt-interval"
              type="number"
              min={5}
              max={1440}
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
            />
            <p className="text-[11px] text-slate-400">5 - 1440 นาที (default 720 = 12 ชม.)</p>
          </div>

          {submitError !== null && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{ytErrorMessage(submitError)}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={create.isPending}
          >
            ยกเลิก
          </Button>
          <Button
            onClick={handleSave}
            disabled={create.isPending || !name.trim() || !playlistInput.trim()}
          >
            {create.isPending ? (
              <>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> กำลังบันทึก
              </>
            ) : (
              'บันทึก'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
